
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/common/LoadingButton";
import { X, Save, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { dialogStyles, buttonStyles, iconStyles } from "@/styles/common-styles";

export type StandardEntityDialogProps = {
  title: string;
  icon?: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  isLoading: boolean;
  children: React.ReactNode;
  onDelete?: () => void;
  // Optional custom left-side footer actions (e.g., extra buttons)
  footerLeft?: React.ReactNode;
  className?: string;
  validationErrors?: string[];
  size?: "sm" | "md" | "standard" | "lg" | "xl";
  saveText?: string;
  cancelText?: string;
};

const sizeClasses = {
  sm: dialogStyles.content,
  md: dialogStyles.contentMedium,
  standard: dialogStyles.contentStandard,
  lg: dialogStyles.contentLarge,
  xl: dialogStyles.contentXLarge
};

export const StandardEntityDialog = ({
  title,
  icon,
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  children,
  onDelete,
  footerLeft,
  className,
  validationErrors = [],
  size = "standard",
  saveText = "Сохранить",
  cancelText = "Отмена"
}: StandardEntityDialogProps) => {
  console.log('[StandardEntityDialog] Render with props:', { title, isOpen, isLoading });
  
  return (
    <Dialog open={isOpen} onOpenChange={() => !isLoading && onClose()}>
      <DialogContent 
        className={cn(
          "max-h-[90vh] overflow-y-auto",
          sizeClasses[size],
          className
        )}
        aria-describedby="dialog-description"
      >
        <DialogHeader className={dialogStyles.header}>
          <DialogTitle className={dialogStyles.title}>
            {icon && <span className={cn(iconStyles.medium, "mr-3 text-primary")}>{icon}</span>}
            {title}
          </DialogTitle>
          <div id="dialog-description" className="sr-only">
            Диалог для {title.toLowerCase()}
          </div>
        </DialogHeader>

        {validationErrors.length > 0 && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 mb-4 mx-4 sm:mx-6">
            <h4 className="font-medium text-destructive mb-2">Ошибки валидации:</h4>
            <ul className="list-disc list-inside text-sm text-destructive">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className={dialogStyles.body}>
          {children}
        </div>

        <form
          onSubmit={(e) => {
            // Prevent native form submission causing full page reload
            e.preventDefault();
            e.stopPropagation();
            // Delegate to consumer handler
            void onSubmit(e);
          }}
        >
          <div className="flex items-center justify-between p-4 sm:p-6 pt-2 border-t">
            {/* Левая часть футера: приоритет у кастомного слота, затем delete */}
            {footerLeft ? (
              <div className="flex items-center gap-2">{footerLeft}</div>
            ) : onDelete ? (
              <Button 
                type="button" 
                variant="destructive" 
                onClick={onDelete} 
                disabled={isLoading}
                className={buttonStyles.actionButton}
              >
                <Trash2 className={iconStyles.small} />
                Удалить
              </Button>
            ) : (
              <div />
            )}
            
            {/* Кнопки отмены и сохранения справа */}
            <div className={buttonStyles.buttonGroup}>
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose} 
                disabled={isLoading}
              >
                <X className={iconStyles.small} />
                {cancelText}
              </Button>
              <LoadingButton 
                type="submit" 
                loading={isLoading}
                className={buttonStyles.actionButton}
              >
                <Save className={iconStyles.small} />
                {saveText}
              </LoadingButton>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
